/**
 * Input Sanitization Utility
 * 
 * Prevents XSS attacks and injection vulnerabilities by sanitizing user input.
 * 
 * Features:
 * - HTML tag removal
 * - Script injection prevention
 * - URL validation and sanitization
 * - Safe whitelist-based HTML rendering
 * - TypeScript type safety
 * 
 * Usage:
 * ```ts
 * import { sanitizeInput, sanitizeHtml, sanitizeUrl } from '@/lib/sanitize';
 * 
 * const safeText = sanitizeInput(userInput);
 * const safeHtml = sanitizeHtml(htmlContent);
 * const safeUrl = sanitizeUrl(urlString);
 * ```
 */

/**
 * Configuration for HTML sanitization
 */
export interface SanitizeConfig {
  allowedTags?: string[];
  allowedAttributes?: Record<string, string[]>;
  maxLength?: number;
}

/**
 * Default safe HTML tags (for sanitizeHtml function)
 */
const DEFAULT_ALLOWED_TAGS = [
  'b', 'i', 'em', 'strong', 'u', 'p', 'br',
  'ul', 'ol', 'li', 'a', 'span'
];

/**
 * Default safe HTML attributes
 */
const DEFAULT_ALLOWED_ATTRIBUTES: Record<string, string[]> = {
  'a': ['href', 'title', 'target'],
  'span': ['class']
};

/**
 * Sanitize basic text input by removing HTML tags and potentially dangerous characters
 * 
 * @param input - Raw user input string
 * @param maxLength - Maximum allowed length (default: 10000)
 * @returns Sanitized string safe for display and storage
 * 
 * @example
 * ```ts
 * const userInput = '<script>alert("xss")</script>Hello';
 * const safe = sanitizeInput(userInput);
 * console.log(safe); // "Hello"
 * ```
 */
export function sanitizeInput(input: string | null | undefined, maxLength = 10000): string {
  if (!input) return '';
  
  // Convert to string and trim
  let sanitized = String(input).trim();
  
  // Enforce max length
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength);
  }
  
  // Remove HTML tags
  sanitized = sanitized.replace(/<[^>]*>/g, '');
  
  // Remove potential script injections
  sanitized = sanitized.replace(/javascript:/gi, '');
  sanitized = sanitized.replace(/on\w+\s*=/gi, '');
  
  // Decode HTML entities to prevent double encoding attacks
  sanitized = decodeHtmlEntities(sanitized);
  
  return sanitized;
}

/**
 * Sanitize HTML content with whitelist-based approach
 * 
 * @param html - Raw HTML string
 * @param config - Configuration for allowed tags and attributes
 * @returns Sanitized HTML string
 * 
 * @example
 * ```ts
 * const userHtml = '<div><script>alert("xss")</script><p>Safe content</p></div>';
 * const safe = sanitizeHtml(userHtml);
 * console.log(safe); // "<p>Safe content</p>"
 * ```
 */
export function sanitizeHtml(
  html: string | null | undefined,
  config: SanitizeConfig = {}
): string {
  if (!html) return '';
  
  const {
    allowedTags = DEFAULT_ALLOWED_TAGS,
    allowedAttributes = DEFAULT_ALLOWED_ATTRIBUTES,
    maxLength = 50000
  } = config;
  
  let sanitized = String(html).trim();
  
  // Enforce max length
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength);
  }
  
  // Remove script tags and content
  sanitized = sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  
  // Remove event handlers
  sanitized = sanitized.replace(/on\w+\s*=\s*["'][^"']*["']/gi, '');
  sanitized = sanitized.replace(/on\w+\s*=\s*[^\s>]*/gi, '');
  
  // Remove javascript: protocol
  sanitized = sanitized.replace(/javascript:/gi, '');
  
  // Remove data: protocol (can be used for XSS)
  sanitized = sanitized.replace(/data:text\/html/gi, '');
  
  // Filter tags - remove tags not in whitelist
  const tagPattern = /<\/?([a-z][a-z0-9]*)\b[^>]*>/gi;
  sanitized = sanitized.replace(tagPattern, (match, tagName) => {
    const tag = tagName.toLowerCase();
    
    // Remove if not in allowed tags
    if (!allowedTags.includes(tag)) {
      return '';
    }
    
    // Filter attributes for allowed tags
    if (allowedAttributes[tag]) {
      const allowedAttrs = allowedAttributes[tag];
      const attrPattern = /(\w+)\s*=\s*["']?([^"']*)["']?/g;
      
      return match.replace(attrPattern, (attrMatch, attrName) => {
        return allowedAttrs.includes(attrName.toLowerCase()) ? attrMatch : '';
      });
    }
    
    return match;
  });
  
  return sanitized;
}

/**
 * Sanitize and validate URLs
 * 
 * @param url - Raw URL string
 * @param allowedProtocols - Allowed URL protocols (default: ['http', 'https', 'mailto'])
 * @returns Sanitized URL or empty string if invalid
 * 
 * @example
 * ```ts
 * const userUrl = 'javascript:alert("xss")';
 * const safe = sanitizeUrl(userUrl);
 * console.log(safe); // ""
 * 
 * const validUrl = 'https://example.com';
 * const safe2 = sanitizeUrl(validUrl);
 * console.log(safe2); // "https://example.com"
 * ```
 */
export function sanitizeUrl(
  url: string | null | undefined,
  allowedProtocols: string[] = ['http', 'https', 'mailto']
): string {
  if (!url) return '';
  
  const sanitized = String(url).trim();
  
  // Check for dangerous protocols
  const dangerousProtocols = ['javascript:', 'data:', 'vbscript:', 'file:'];
  const lowerUrl = sanitized.toLowerCase();
  
  for (const protocol of dangerousProtocols) {
    if (lowerUrl.startsWith(protocol)) {
      return '';
    }
  }
  
  // Validate URL format
  try {
    const urlObj = new URL(sanitized);
    
    // Check if protocol is allowed
    const protocol = urlObj.protocol.replace(':', '');
    if (!allowedProtocols.includes(protocol)) {
      return '';
    }
    
    return urlObj.toString();
  } catch (e) {
    // Not a valid absolute URL, check if it's a relative URL
    if (sanitized.startsWith('/') || sanitized.startsWith('./') || sanitized.startsWith('../')) {
      // Relative URLs are safe
      return sanitized;
    }
    
    // Invalid URL
    return '';
  }
}

/**
 * Sanitize filename for safe file operations
 * 
 * @param filename - Raw filename
 * @returns Sanitized filename
 * 
 * @example
 * ```ts
 * const userFilename = '../../../etc/passwd';
 * const safe = sanitizeFilename(userFilename);
 * console.log(safe); // "etcpasswd"
 * ```
 */
export function sanitizeFilename(filename: string | null | undefined): string {
  if (!filename) return '';
  
  let sanitized = String(filename).trim();
  
  // Remove path traversal attempts
  sanitized = sanitized.replace(/\.\./g, '');
  sanitized = sanitized.replace(/[/\\]/g, '');
  
  // Remove special characters that could cause issues
  sanitized = sanitized.replace(/[<>:"|?*]/g, '');
  
  // Limit length
  if (sanitized.length > 255) {
    const ext = sanitized.split('.').pop();
    const nameWithoutExt = sanitized.substring(0, 255 - (ext ? ext.length + 1 : 0));
    sanitized = ext ? `${nameWithoutExt}.${ext}` : nameWithoutExt;
  }
  
  return sanitized;
}

/**
 * Sanitize email address
 * 
 * @param email - Raw email string
 * @returns Sanitized email or empty string if invalid
 * 
 * @example
 * ```ts
 * const userEmail = 'test@example.com<script>alert("xss")</script>';
 * const safe = sanitizeEmail(userEmail);
 * console.log(safe); // "test@example.com"
 * ```
 */
export function sanitizeEmail(email: string | null | undefined): string {
  if (!email) return '';
  
  let sanitized = String(email).trim().toLowerCase();
  
  // Remove dangerous characters but allow incomplete emails during typing
  sanitized = sanitized.replace(/<[^>]*>/g, '');
  sanitized = sanitized.replace(/javascript:/gi, '');
  sanitized = sanitized.replace(/on\w+\s*=/gi, '');
  
  // Only allow valid email characters (letters, numbers, @, ., -, _, +, %)
  sanitized = sanitized.replace(/[^a-z0-9@.\-_+%]/g, '');
  
  return sanitized;
}

/**
 * Decode HTML entities to prevent double-encoding attacks
 * 
 * @param text - Text with HTML entities
 * @returns Decoded text
 */
function decodeHtmlEntities(text: string): string {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
}

/**
 * Escape HTML for safe rendering in HTML context
 * Alternative to sanitization when you want to display the exact input
 * 
 * @param text - Text to escape
 * @returns HTML-escaped text
 * 
 * @example
 * ```ts
 * const userInput = '<script>alert("xss")</script>';
 * const escaped = escapeHtml(userInput);
 * console.log(escaped); // "&lt;script&gt;alert(&quot;xss&quot;)&lt;/script&gt;"
 * ```
 */
export function escapeHtml(text: string | null | undefined): string {
  if (!text) return '';
  
  const div = document.createElement('div');
  div.textContent = String(text);
  return div.innerHTML;
}

/**
 * Check if a string contains potential XSS patterns
 * 
 * @param input - String to check
 * @returns true if suspicious patterns detected
 */
export function containsXssPatterns(input: string): boolean {
  if (!input) return false;
  
  const xssPatterns = [
    /<script/i,
    /javascript:/i,
    /on\w+\s*=/i,
    /<iframe/i,
    /<object/i,
    /<embed/i,
    /eval\(/i,
    /expression\(/i,
  ];
  
  return xssPatterns.some(pattern => pattern.test(input));
}
